import React, { useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
  Alert,
  Chip,
} from '@mui/material'
import { Search, Mic, Star, Users, Calendar, ArrowRight } from 'lucide-react'
import { LOCAL, DEVELOP } from 'constants/env'

const config = process.env.NODE_ENV === 'development' ? LOCAL : DEVELOP

export default function PodcastAnalyzer(): JSX.Element {
  const [podcastName, setPodcastName] = useState('')
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)

  const handleAnalyze = async (): Promise<void> => {
    if (!podcastName.trim()) return
    setLoading(true)
    setError(null)
    setData(null)

    try {
      const response = await fetch(`${config.API_URL}/api/analyze/podcast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ podcastName: podcastName.trim() }),
      })
      const result = await response.json()
      if (!response.ok || !result.success) {
        throw new Error(result.error ?? 'Analysis failed')
      }
      setData(result.data)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#0f172a', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', pt: { xs: 4, md: 6 }, pb: 3, px: 2 }}>
        <Typography sx={{
          fontSize: { xs: '28px', md: '40px' }, fontWeight: 800,
          background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 50%, #6366f1 100%)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', mb: 1,
        }}>
          Free Podcast Analyzer
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '16px', maxWidth: 500, mx: 'auto' }}>
          See your ratings, reviews, and how you compare to similar podcasts — in seconds.
        </Typography>
      </Box>

      <Box sx={{ width: '100%', maxWidth: 640, px: 2, pb: 8 }}>
        {/* Search */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Enter your podcast name..."
            value={podcastName}
            onChange={(e) => setPodcastName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { handleAnalyze().catch(console.error) } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Mic size={18} color="rgba(255,255,255,0.4)" /></InputAdornment>,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'white', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '12px',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                '&:hover fieldset': { borderColor: 'rgba(168,85,247,0.4)' },
                '&.Mui-focused fieldset': { borderColor: '#a855f7' },
              },
            }}
          />
          <Button
            onClick={() => { handleAnalyze().catch(console.error) }}
            disabled={!podcastName.trim() || loading}
            startIcon={loading ? <CircularProgress size={18} /> : <Search size={18} />}
            sx={{ background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)', color: 'white', borderRadius: '12px', textTransform: 'none', px: 3, fontWeight: 600, whiteSpace: 'nowrap', '&:disabled': { opacity: 0.5, color: 'white' } }}
          >
            Analyze
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{error}</Alert>}

        {loading && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#a855f7', mb: 2 }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>Analyzing your podcast...</Typography>
          </Box>
        )}

        {data?.podcast && (
          <>
            {/* Podcast card */}
            <Box sx={{ p: 3, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'center', mb: 2 }}>
                {data.podcast.imageUrl && <Box component="img" src={data.podcast.imageUrl} sx={{ width: 72, height: 72, borderRadius: '14px', objectFit: 'cover', border: '2px solid rgba(168,85,247,0.3)' }} />}
                <Box>
                  <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '20px', mb: 0.5 }}>{data.podcast.title}</Typography>
                  {data.podcast.ratingAverage != null && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Star size={16} color="#f59e0b" fill="#f59e0b" />
                      <Typography sx={{ color: '#f59e0b', fontWeight: 700, fontSize: '16px' }}>{String(data.podcast.ratingAverage.toFixed(1))}</Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px' }}>({String(data.podcast.ratingCount)} ratings · {String(data.podcast.reviewCount)} reviews)</Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Stats grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '24px' }}>{String(data.podcast.numberOfEpisodes)}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>Episodes</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '24px' }}>{data.podcast.avgEpisodeLength ? `${String(data.podcast.avgEpisodeLength)}m` : '—'}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>Avg Length</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '24px' }}>{data.podcast.daysBetweenEpisodes ? `${String(data.podcast.daysBetweenEpisodes)}d` : '—'}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>Between Episodes</Typography>
                </Box>
              </Box>
            </Box>

            {/* Reviews */}
            {data.reviews?.length > 0 && (
              <Box sx={{ p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', mb: 2 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 2 }}>Recent Reviews</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {data.reviews.map((review: any) => (
                    <Box key={review.id} sx={{ p: 1.5, backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        {Array.from({ length: review.rating }).map((_, i) => <Star key={i} size={12} color="#f59e0b" fill="#f59e0b" />)}
                        {review.author?.name && <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', ml: 1 }}>by {review.author.name}</Typography>}
                      </Box>
                      {review.body && <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px', lineHeight: 1.5 }}>{review.body}</Typography>}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Similar podcasts */}
            {data.similarPodcasts?.length > 0 && (
              <Box sx={{ p: 2.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', mb: 2 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 2 }}>Similar Podcasts</Typography>
                <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1 }}>
                  {data.similarPodcasts.map((pod: any) => (
                    <Box key={pod.id} sx={{ minWidth: 130, p: 1.5, backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', flexShrink: 0, textAlign: 'center' }}>
                      {pod.imageUrl && <Box component="img" src={pod.imageUrl} sx={{ width: 44, height: 44, borderRadius: '10px', objectFit: 'cover', mx: 'auto', mb: 0.5 }} />}
                      <Typography sx={{ color: 'white', fontSize: '11px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pod.title}</Typography>
                      {pod.ratingAverage && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center', mt: 0.25 }}>
                          <Star size={10} color="#f59e0b" fill="#f59e0b" />
                          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px' }}>{String(pod.ratingAverage.toFixed(1))}</Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* CTA */}
            <Box sx={{ mt: 3, p: 3, background: 'linear-gradient(135deg, rgba(168,85,247,0.15) 0%, rgba(99,102,241,0.15) 100%)', border: '1px solid rgba(168,85,247,0.3)', borderRadius: '16px', textAlign: 'center' }}>
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '18px', mb: 1 }}>Want to grow your podcast audience?</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', mb: 2 }}>Nalapod finds conversations about your topics on social media and helps you engage authentically — on autopilot.</Typography>
              <Button href="https://app.nalapod.com/dashboard" endIcon={<ArrowRight size={16} />} sx={{ background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)', color: 'white', borderRadius: '12px', textTransform: 'none', px: 4, py: 1.2, fontWeight: 600 }}>
                Start Free Trial
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}
